import { useCallback, useMemo } from 'react';
import { useGetModuleQuery } from '@platform/state/api';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { statusColor } from '@platform/core/lib/statusColors';
import type { IRecordTypeLabels } from './IRecordTypeLabels';

/**
 * Локалізовані статуси й варіанти полів типу запису для сторінок поза модулем (пошук, «Моя робота», огляд напряму): визначення модуля
 * GET /meta/modules/{ключ}?workspaceId= (злите з налаштуваннями простору маршруту) кешується RTK Query, тож групи одного модуля роблять один запит. Невідоме значення показується як є.
 */
export function useRecordTypeLabels(module: string | null | undefined, recordType: string | null | undefined): IRecordTypeLabels {
  const label = useLabel();
  const { ws } = useWorkspaceParams();
  const { data } = useGetModuleQuery({ key: module ?? '', workspaceId: ws || undefined }, { skip: !module });
  const rt = useMemo(() => data?.recordTypes.find((r) => r.key === recordType), [data, recordType]);
  const status = useCallback((value: string | null | undefined) => {
    const def = rt?.statuses.list.find((s) => s.value === value);
    return { label: label(def?.label) || value || '', color: rt && value ? statusColor(rt.statuses, value, rt.key) : 'var(--line2)' };
  }, [label, rt]);
  const option = useCallback((field: string, value: unknown) => {
    if (typeof value !== 'string' || !value) return '';
    return label(rt?.fields.find((f) => f.key === field)?.options?.find((o) => o.value === value)?.label) || value;
  }, [label, rt]);
  return useMemo(() => ({ status, option }), [status, option]);
}
